import PropTypes from "prop-types";
import { useEffect, useId, useRef } from "react";

// Menu suspenso do "Baixar relatório": cartão branco ancorado no botão, no mesmo padrão
// dos popovers do painel (borda clara, sombra, rótulo de seção em roxo). Fecha com Esc,
// com clique fora e ao escolher um item; o foco volta para o botão que o abriu.
const ReportDownloadMenu = ({ sections, onSelect, onClose, triggerRef }) => {
  const menuRef = useRef(null);
  const idPrefix = useId();

  useEffect(() => {
    menuRef.current?.querySelector('[role="menuitem"]')?.focus();

    const closeOnOutsideClick = (event) => {
      if (menuRef.current?.contains(event.target)) return;
      if (triggerRef?.current?.contains(event.target)) return;
      onClose();
    };
    const closeOnEscape = (event) => {
      if (event.key !== "Escape") return;
      onClose();
      triggerRef?.current?.focus();
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [onClose, triggerRef]);

  // Setas percorrem os itens, como num menu nativo.
  const moveFocus = (event) => {
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    const items = [...menuRef.current.querySelectorAll('[role="menuitem"]')];
    const current = items.indexOf(document.activeElement);
    const next = {
      ArrowDown: (current + 1) % items.length,
      ArrowUp: (current - 1 + items.length) % items.length,
      Home: 0,
      End: items.length - 1,
    }[event.key];
    items[next]?.focus();
  };

  return (
    <div
      className="report-menu"
      role="menu"
      aria-label="Relatórios disponíveis"
      ref={menuRef}
      onKeyDown={moveFocus}
    >
      {sections.filter((section) => section.items.length > 0).map((section) => (
        <div className="report-menu__section" role="group" aria-label={section.title} key={section.title}>
          <span className="report-menu__eyebrow" aria-hidden="true">{section.title}</span>
          {section.items.map((item) => {
            const descriptionId = item.description ? `${idPrefix}-${item.name}` : undefined;
            return (
              <button
                type="button"
                role="menuitem"
                className="report-menu__item"
                key={item.name}
                aria-label={item.label}
                aria-describedby={descriptionId}
                onClick={(event) => onSelect(item.name, event)}
              >
                <span className="report-menu__label">{item.label}</span>
                {item.description ? (
                  <span className="report-menu__description" id={descriptionId}>{item.description}</span>
                ) : null}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
};

ReportDownloadMenu.propTypes = {
  sections: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string.isRequired,
    items: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      description: PropTypes.string,
    })).isRequired,
  })).isRequired,
  onSelect: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  triggerRef: PropTypes.shape({ current: PropTypes.any }),
};

export default ReportDownloadMenu;
